import React, { useContext } from 'react';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import BuildingTable, {
  useBuildingRows,
} from '@totem/components/deviceEndOfSupport/buildingTable';
import DataNotes from '@totem/components/deviceEndOfSupport/dataNotes';
import DeviceEndOfSupportContext from '@totem/components/deviceEndOfSupport/deviceEndOfSupportContext';
import EndOfSupportTable from '@totem/components/deviceEndOfSupport/endOfSupportTable';
import KpiTiles from '@totem/components/deviceEndOfSupport/kpiTiles';
import {
  CSV_HEADERS,
  FILTER_LABEL,
  FilterKey,
  toCsvRow,
} from '@totem/components/deviceEndOfSupport/utilities';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { exportRecordsToCsv } from '@totem/utilities/fileUtilities';

import './deviceEndOfSupport.css';

export const DeviceEndOfSupportExtra = () => {
  const { rows, loading, onRefresh } = useContext(DeviceEndOfSupportContext);

  return (
    <Space>
      <RecordCountBadge count={rows.length} />
      <Button
        icon={<ReloadOutlined />}
        loading={loading}
        onClick={onRefresh}
        title="Refresh"
      />
    </Space>
  );
};

const CoverageBanner = () => {
  const { coverage, setActiveTab } = useContext(DeviceEndOfSupportContext);
  if (!coverage) {
    return null;
  }
  const unrated =
    coverage.withoutRecord.managed + coverage.withoutRecord.notManaged;

  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message={
        <>
          <strong>
            {unrated.toLocaleString('en-US')} other devices have no lifecycle
            record
          </strong>{' '}
          ({coverage.withoutRecord.managed} under IB management) and are not
          listed here. Dates shown are estimated automatically; see{' '}
          <Button
            type="link"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => setActiveTab('notes')}
          >
            Data notes
          </Button>
          .
        </>
      }
    />
  );
};

const FilterChips = () => {
  const { filters, setFilter, search, setSearch, resetAll } = useContext(
    DeviceEndOfSupportContext,
  );

  const chips: { key: string; label: string; onClose: () => void }[] = (
    Object.keys(filters) as FilterKey[]
  )
    .filter((key) => filters[key]?.length)
    .map((key) => {
      const values = filters[key];
      const shown =
        values.length > 2
          ? `${values.slice(0, 2).join(', ')} +${values.length - 2}`
          : values.join(', ');
      return {
        key,
        label: `${FILTER_LABEL[key]}: ${shown}`,
        onClose: () => setFilter(key, []),
      };
    });
  if (search.trim()) {
    chips.unshift({
      key: 'search',
      label: `Device: “${search.trim()}”`,
      onClose: () => setSearch(''),
    });
  }
  if (chips.length === 0) {
    return null;
  }

  return (
    <div styleName="chips">
      {chips.map((chip) => (
        <Tag
          key={chip.key}
          closable
          onClose={(event) => {
            event.preventDefault();

            chip.onClose();
          }}
        >
          {chip.label}
        </Tag>
      ))}
      <Button type="link" size="small" onClick={resetAll}>
        Reset all
      </Button>
    </div>
  );
};

const DevicesTab = () => {
  const { rows, filteredRows, search, setSearch } = useContext(
    DeviceEndOfSupportContext,
  );

  return (
    <>
      <KpiTiles />
      <CoverageBanner />
      <FilterChips />
      <div styleName="toolbar">
        <Input.Search
          allowClear
          placeholder="Search name, type, vendor"
          style={{ width: 260 }}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Typography.Text type="secondary" styleName="toolbar-count">
          {filteredRows.length} of {rows.length} devices
        </Typography.Text>
        <Button
          icon={<DownloadOutlined />}
          disabled={filteredRows.length === 0}
          onClick={() =>
            exportRecordsToCsv(
              'device_end_of_support',
              CSV_HEADERS,
              filteredRows.map(toCsvRow),
            )
          }
        >
          Export CSV
        </Button>
      </div>
      <EndOfSupportTable />
    </>
  );
};

const DeviceEndOfSupportLayout = () => {
  const { rows, activeTab, setActiveTab } = useContext(
    DeviceEndOfSupportContext,
  );
  const buildings = useBuildingRows();

  return (
    <>
      <IBTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'devices',
            label: `Devices (${rows.length})`,
            children: <DevicesTab />,
          },
          {
            key: 'buildings',
            label: `By building (${buildings.length})`,
            children: <BuildingTable />,
          },
          { key: 'notes', label: 'Data notes', children: <DataNotes /> },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ fontSize: 11 }}>
        Figures cover equipment currently deployed; decommissioned devices are
        excluded. Status is worked out from each device&rsquo;s End of Support
        date as of {dayjs().format('MMMM D, YYYY')}. Source: IB Portal device
        inventory.
      </Typography.Paragraph>
    </>
  );
};

export default DeviceEndOfSupportLayout;
